import React from "react";

// Article 8 — Oral Thrush in Pregnancy: Is It Harmful to the Baby—or a Sign Something Else Needs Checking?
// Content is verbatim from the approved brief. Do not paraphrase, shorten, expand
// or add FAQs, sticky contents menu, ratings, comments, popularity counters or
// unrelated call-to-action. Medical-review line is withheld.

function Cite({ n }) {
  const first = String(n).split(",")[0];
  return (
    <sup className="text-prestige font-medium">
      <a
        href={`#ref-${first}`}
        aria-label={`See reference ${first}`}
        className="hover:underline"
      >{`[${n}]`}</a>
    </sup>
  );
}

const H2 = ({ children }) => (
  <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-12">
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="mt-4 text-[15px] text-stone2 leading-relaxed">{children}</p>
);

export function OralThrushPregnancyCandidiasis() {
  return (
    <article className="mt-10">
      <P>
        Localised oral thrush is usually uncomfortable rather than dangerous to the pregnancy. It does not usually mean that Candida has entered the bloodstream, crossed the placenta or infected the baby. However, oral thrush is uncommon in otherwise healthy adults, so persistent or recurrent disease deserves more than repeated self-treatment.
      </P>
      <P>
        The useful question is: Is this truly oral candidiasis, what allowed it to develop, and can treatment remain local while the trigger is corrected?
      </P>

      <H2>Oral Thrush Is Not the Same as Vaginal Thrush</H2>
      <P>
        Candida normally lives in the mouth, gut and genital tract without causing disease. Thrush develops when the yeast overgrows. Pregnancy clearly increases the risk of vaginal candidiasis. It does not make oral thrush an expected pregnancy symptom. In healthy adults, candidiasis of the mouth and throat is much less common than vaginal thrush. <Cite n="1" /> Vaginal itching or discharge and white patches inside the mouth should therefore be assessed as separate problems rather than assumed to be one spreading infection.
      </P>

      <H2>A White Tongue Is Not Automatically Thrush</H2>
      <P>
        Typical oral thrush may cause creamy white patches on the tongue, inner cheeks, palate or throat, with redness, burning, altered taste or cracks at the mouth corners. When wiped, a plaque may leave a sore red surface that can bleed. <Cite n="2,3" /> A coated tongue, mouth ulcer, geographic tongue, lichen planus, leukoplakia and irritation from dental appliances can look similar. A white patch that cannot be wiped away, persists or looks unusual needs dental or medical examination. When infection recurs or treatment fails, a clinician may take a scraping or swab and reconsider the diagnosis instead of simply prescribing another antifungal.
      </P>

      <H2>Does Oral Thrush Harm the Baby?</H2>
      <P>
        Uncomplicated oral thrush is a local mucosal infection. It is not the same as invasive candidiasis, in which Candida enters the bloodstream or internal organs. Invasive disease occurs mainly in seriously ill or hospitalised patients and is not the usual concern in a well pregnant woman with mouth patches. <Cite n="1,2" /> Oral thrush alone does not normally require extra fetal scans. Concern arises if severe pain prevents eating, drinking or taking essential medicines, or if thrush reveals poorly controlled diabetes or significant immune suppression.
      </P>

      <H2>Why Did It Develop?</H2>
      <P>
        Common triggers include recent antibiotics, inhaled or systemic corticosteroids, diabetes, dry mouth, smoking, dentures and medicines or illnesses that weaken immune control. <Cite n="1" /> After a steroid inhaler, rinsing the mouth and checking inhaler technique can reduce recurrence. Dentures require careful cleaning and should not remain in overnight. Persistent or recurrent thrush may justify checking glucose control and, according to the clinical history, looking for another underlying condition. Pregnancy should not be blamed automatically when a more correctable explanation is present.
      </P>

      <H2>Treatment in Pregnancy Should Stay Local When Possible</H2>
      <P>
        Treatment begins by confirming candidiasis and judging its severity. For mild oral disease, a locally acting antifungal such as nystatin may be selected. NHS pregnancy guidance states that nystatin can be used during pregnancy. <Cite n="4" /> The medicine must contact the affected oral surfaces and be used for the complete prescribed course; stopping as soon as symptoms improve can allow recurrence. Never place leftover vaginal creams or pessaries in the mouth. Oral fluconazole is not routine self-treatment in pregnancy. UKTIS advises that it is not generally recommended when effective local alternatives are available because pregnancy data differ by dose, duration and timing. <Cite n="5" /> Previous standard-dose exposure is not, by itself, a reason to end the pregnancy, but the exact exposure should be reviewed.
      </P>

      <H2>Why Does Thrush Keep Returning?</H2>
      <P>
        Recurrence can result from an incorrect diagnosis, an unfinished course, continued antibiotics, steroid residue after inhaler use, dry mouth, unclean dentures, uncontrolled glucose or an untreated underlying illness. Repeated systemic azole exposure can also select less-susceptible Candida. General candidiasis guidance therefore supports reassessing recurrent or treatment-resistant disease rather than endlessly repeating the same medicine. <Cite n="6" />
      </P>

      <H2>Seek Prompt Assessment</H2>
      <P>
        Seek prompt medical or dental assessment for pain or difficulty swallowing, food feeling stuck, inability to eat or drink, dehydration, fever, chills, rapidly worsening lesions, significant immune suppression or failure to improve. Painful swallowing may mean candidiasis has extended into the oesophagus, which is a different condition and usually requires systemic treatment. <Cite n="2,6" /> A persistent non-wipeable white patch must also be examined rather than repeatedly labelled as thrush. Reduced fetal movements or any acute maternal illness should be assessed through the appropriate maternity service.
      </P>

      <div className="mt-10 bg-surfacealt/60 border-l-2 border-prestige p-6" data-testid="article-takeaway">
        <p className="text-[15px] text-navy leading-relaxed font-semibold">
          Dr Tania’s takeaway: Oral thrush usually does not infect the baby, but repeated oral thrush in a pregnant adult can be a clue. Confirm the diagnosis, use pregnancy-appropriate local treatment when possible, and identify the trigger instead of repeatedly treating the white patch alone.
        </p>
      </div>

      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-16" id="evidence-base">
        Evidence Base
      </h2>
      <ol className="mt-6 space-y-5 list-none pl-0" data-testid="article-references">
        <li id="ref-1" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[1] Centers for Disease Control and Prevention. Risk Factors for Candidiasis. Updated April 24, 2024.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://www.cdc.gov/candidiasis/risk-factors/index.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://www.cdc.gov/candidiasis/risk-factors/index.html</a>
          </p>
        </li>
        <li id="ref-2" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[2] Centers for Disease Control and Prevention. Signs and Symptoms of Candidiasis. Updated April 24, 2024.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://www.cdc.gov/candidiasis/signs-symptoms/index.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://www.cdc.gov/candidiasis/signs-symptoms/index.html</a>
          </p>
        </li>
        <li id="ref-3" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[3] National Health Service. Oral thrush (mouth thrush). Current online patient guidance.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://www.nhs.uk/conditions/oral-thrush-mouth-thrush/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://www.nhs.uk/conditions/oral-thrush-mouth-thrush/</a>
          </p>
        </li>
        <li id="ref-4" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[4] National Health Service. Pregnancy, breastfeeding and fertility while taking or using nystatin. Last reviewed August 1, 2023.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://www.nhs.uk/medicines/nystatin/pregnancy-breastfeeding-and-fertility-while-taking-or-using-nystatin/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://www.nhs.uk/medicines/nystatin/pregnancy-breastfeeding-and-fertility-while-taking-or-using-nystatin/</a>
          </p>
        </li>
        <li id="ref-5" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[5] UK Teratology Information Service. Use of Fluconazole in Pregnancy. Version 4. November 2024.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://uktis.org/monographs/use-of-fluconazole-in-pregnancy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://uktis.org/monographs/use-of-fluconazole-in-pregnancy/</a>
          </p>
        </li>
        <li id="ref-6" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[6] Pappas PG, Kauffman CA, Andes DR, et al. Clinical Practice Guideline for the Management of Candidiasis: 2016 Update by the Infectious Diseases Society of America. <em>Clinical Infectious Diseases</em>. 2016;62(4):e1–e50. doi:10.1093/cid/civ933.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://www.idsociety.org/practice-guideline/candidiasis/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://www.idsociety.org/practice-guideline/candidiasis/</a>
          </p>
        </li>
      </ol>

      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-16" id="medical-disclaimer">
        Medical Disclaimer
      </h2>
      <P>
        This Article provides general education and does not replace patient-specific obstetric, medical, dental or infectious-disease advice. Do not diagnose a white oral patch from a photograph or start oral antifungal tablets because of this page. Seek prompt care for painful or difficult swallowing, inability to eat or drink, dehydration, fever, a persistent non-wipeable patch, reduced fetal movements or feeling seriously unwell.
      </P>
    </article>
  );
}
